import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Modal } from "@legal-500/legal500-design-system";
import { companies, type Company, type Office } from "@/api/addressBook.service";
import { contacts as contactsService, type Contact } from "@/api/contacts.service";
import { loadAllOfficesForCompany } from "@/api/loadAllOfficesForCompany";
import {
  submissionsService,
  type ContactMatchCandidate,
  type Submission,
  type SubmissionEntry,
} from "@/api/submissions.service";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/routes";
import { getSubmissionPracticeFullName } from "@/utils/practiceName";
import { ContactHistory } from "./ContactHistory";
import { ContactMatchPill } from "./ContactMatchPill";
import {
  CONTACT_LINK_STATE_LABELS,
  SUBMISSION_READ_PERMISSION,
  buildCreateContactUrl,
  getContactLinkState,
  sameCountry,
} from "./contactMatch";
import badgeStyles from "./ContactMatch.module.css";
import styles from "./LinkSubmissionContactModal.module.css";

/** Same rhythm and page size as the Awards LinkLawyerModal this is modelled on. */
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;
const ANY_COUNTRY = "";
/**
 * Most ranked contact ids sent as the `ids` filter. They travel in the query string
 * (37 characters each), and Node's default 16 KB header limit caps the whole request
 * line, so 300 ids (~11 KB) leaves headroom. A firm's ranked lawyers in one practice
 * area are normally far fewer; past the cap the filter just narrows to the first 300.
 */
const RANKED_IDS_CAP = 300;

type EntryContactState = Pick<
  SubmissionEntry,
  "contactId" | "contactName" | "contactMatchStatus" | "contactMatchCandidates"
>;

type CompanyState =
  | { status: "loading" }
  | { status: "ready"; company: Company }
  | { status: "no_firm_ref" }
  | { status: "not_found" }
  | { status: "error" };

type CountryOption = { id: number; name: string };

type RankedState =
  | { status: "loading" }
  | { status: "ready"; available: boolean; contactIds: string[] };

type BusyAction = { kind: "link"; contactId: string } | { kind: "clear" } | { kind: "rematch" };

export type LinkSubmissionContactModalProps = {
  /** The submissions-list row (one lawyer entry). Must be a lawyer/lawyer submission. */
  submission: Submission;
  /** A contact created via "Create new contact" and handed back by ContactDetail. */
  preselectedContactId?: string | null;
  /** The list URL to come back to after creating a contact (already carries the entry id). */
  returnTo: string | null;
  onClose: () => void;
  /** Called after any successful write so the list underneath can refetch. */
  onChanged: () => void;
};

function errorMessageFor(err: unknown, fallback: string): string {
  const { code, status } = (err ?? {}) as { code?: string; status?: number };
  if (code === "contact_not_found") {
    return "That contact is no longer in the Address Book. Refresh the list and pick another.";
  }
  if (code === "not_lawyer_submission") {
    return "Only lawyer-recommended submissions can be linked to a contact.";
  }
  if (status === 403) return "You don't have permission to change this submission's contact.";
  return fallback;
}

/** Distinct countries of the firm's offices, alphabetically. */
function countriesOf(offices: Office[]): CountryOption[] {
  const byId = new Map<number, string>();
  for (const office of offices) {
    if (office.countryId !== null && office.countryName) {
      byId.set(office.countryId, office.countryName);
    }
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The office "Create new contact" should preselect: one in the category country, head office first. */
function officeForCountry(offices: Office[], country: string | null | undefined): Office | null {
  const inCountry = offices.filter(office => sameCountry(office.countryName, country));
  return inCountry.find(office => office.isHeadOffice) ?? inCountry[0] ?? null;
}

function EvidenceBadges({ candidate }: { candidate: ContactMatchCandidate }) {
  return (
    <span className={styles.badges}>
      {candidate.rankedInCategory === true && (
        <span className={`${badgeStyles.badge} ${badgeStyles.badgePositive}`}>
          Ranked in this area
        </span>
      )}
      {candidate.countryMatch && (
        <span className={`${badgeStyles.badge} ${badgeStyles.badgePositive}`}>Same country</span>
      )}
      {!candidate.activeAtFirm && (
        <span
          className={`${badgeStyles.badge} ${badgeStyles.badgeCaution}`}
          title="No active role at this firm"
        >
          Former role
        </span>
      )}
    </span>
  );
}

/**
 * Links a lawyer-recommended submission entry to an Address Book contact
 * (DEV-2662). Modelled on the Awards LinkLawyerModal:
 * seeded, debounced, firm-scoped search with a stale-response guard and errors kept inside
 * the modal. On top of that it shows the matcher's stored candidates with their evidence,
 * filters by country / active role / ranking, and can record "no matching contact" or
 * re-run the auto-match.
 */
export function LinkSubmissionContactModal({
  submission,
  preselectedContactId,
  returnTo,
  onClose,
  onChanged,
}: LinkSubmissionContactModalProps) {
  const submissionId = submission.submissionId ?? "";
  const entryId = submission.submissionEntryId ?? "";
  const personName = submission.personName ?? "";
  const categoryCountry = submission.regionName ?? null;
  const practiceAreaId = submission.practiceAreaId ?? null;

  const [entry, setEntry] = useState<EntryContactState>({
    contactId: submission.contactId ?? null,
    contactName: submission.contactName ?? null,
    contactMatchStatus: submission.contactMatchStatus ?? "pending",
    contactMatchCandidates: submission.contactMatchCandidates ?? null,
  });

  const [companyState, setCompanyState] = useState<CompanyState>(
    submission.firmRef ? { status: "loading" } : { status: "no_firm_ref" },
  );
  const [offices, setOffices] = useState<Office[]>([]);
  const [officesLoaded, setOfficesLoaded] = useState(false);
  const [officesFailed, setOfficesFailed] = useState(false);

  const [searchTerm, setSearchTerm] = useState(personName);
  const [countryId, setCountryId] = useState<string>(ANY_COUNTRY);
  const [activeOnly, setActiveOnly] = useState(true);
  const [rankedOnly, setRankedOnly] = useState(false);
  const [rankedState, setRankedState] = useState<RankedState>({ status: "loading" });
  const [refreshCount, setRefreshCount] = useState(0);

  const [results, setResults] = useState<Contact[]>([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [preselected, setPreselected] = useState<Contact | null>(null);

  const [busy, setBusy] = useState<BusyAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const { hasPermission } = usePermissions();
  const canReadHistory = hasPermission(SUBMISSION_READ_PERMISSION);

  const company = companyState.status === "ready" ? companyState.company : null;
  const companyId = company?.id ?? null;
  const ready = companyId !== null && officesLoaded;
  const firmLoading =
    companyState.status === "loading" || (companyState.status === "ready" && !officesLoaded);
  const rankedAvailable = rankedState.status === "ready" && rankedState.available;
  const rankedIdSet = useMemo(
    () => (rankedState.status === "ready" ? new Set(rankedState.contactIds) : null),
    [rankedState],
  );
  const rankedIdsForFilter = useMemo(
    () => (rankedState.status === "ready" ? rankedState.contactIds.slice(0, RANKED_IDS_CAP) : null),
    [rankedState],
  );

  // The debounced search reads the latest term and filters from here, so a timer set
  // before a filter change never searches with the old filters.
  const latest = useRef({
    searchTerm,
    countryId,
    activeOnly,
    rankedOnly,
    rankedIds: rankedIdsForFilter,
  });
  latest.current = {
    searchTerm,
    countryId,
    activeOnly,
    rankedOnly,
    rankedIds: rankedIdsForFilter,
  };

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  // 1. firm ref → Address Book company → its offices (countries + create-contact office).
  useEffect(() => {
    const firmRef = submission.firmRef;
    if (!firmRef) return;
    let cancelled = false;

    (async () => {
      try {
        const found = await companies.getByFirmRef(firmRef);
        if (cancelled) return;
        if (!found) {
          setCompanyState({ status: "not_found" });
          return;
        }
        setCompanyState({ status: "ready", company: found });
        try {
          const loaded = await loadAllOfficesForCompany(found.id);
          if (cancelled) return;
          setOffices(loaded);
          // Default the country filter to the submission's category country when the
          // firm has an office there (Mike, DEV-2662); otherwise search every country.
          const match = loaded.find(
            office => office.countryId !== null && sameCountry(office.countryName, categoryCountry),
          );
          if (match?.countryId != null) setCountryId(String(match.countryId));
        } catch {
          if (!cancelled) setOfficesFailed(true);
        } finally {
          if (!cancelled) setOfficesLoaded(true);
        }
      } catch {
        if (!cancelled) setCompanyState({ status: "error" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [submission.firmRef, categoryCountry]);

  // 2. A contact just created through "Create new contact": show it first.
  useEffect(() => {
    if (!preselectedContactId) return;
    let cancelled = false;
    contactsService
      .getById(preselectedContactId)
      .then(contact => {
        if (!cancelled) setPreselected(contact);
      })
      .catch(() => {
        // Not fatal: it is still findable through the search below.
      });
    return () => {
      cancelled = true;
    };
  }, [preselectedContactId]);

  // 3. Contacts ranked for this firm in this practice area, resolved by submission-api
  // (staff.submission.read — researchers don't hold data-marts.read). Drives the "Ranked
  // in practice area" filter and the "Ranked in this area" badge on search results.
  useEffect(() => {
    if (!submissionId) {
      setRankedState({ status: "ready", available: false, contactIds: [] });
      return;
    }
    let cancelled = false;
    submissionsService
      .getRankedContacts(submissionId)
      .then(response => {
        if (cancelled) return;
        setRankedState({
          status: "ready",
          available: response.available,
          contactIds: response.contactIds,
        });
      })
      .catch(() => {
        if (!cancelled) setRankedState({ status: "ready", available: false, contactIds: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  const search = useCallback(async () => {
    if (!companyId) return;
    const filters = latest.current;
    const requestId = ++requestIdRef.current;

    if (filters.rankedOnly && filters.rankedIds === null) {
      // Still loading the ranked ids; the effect below re-runs when they arrive.
      setSearching(true);
      return;
    }
    if (filters.rankedOnly && filters.rankedIds?.length === 0) {
      // `ids=` with nothing in it would be a 400 upstream; the answer is simply "nobody".
      setResults([]);
      setTotalMatches(0);
      setSearching(false);
      setSearchError(null);
      return;
    }

    setSearching(true);
    setSearchError(null);
    try {
      const response = await contactsService.getAll({
        companyId,
        search: filters.searchTerm.trim() || undefined,
        countryId: filters.countryId === ANY_COUNTRY ? undefined : Number(filters.countryId),
        activeRolesOnly: filters.activeOnly,
        ids: filters.rankedOnly ? (filters.rankedIds ?? undefined) : undefined,
        limit: SEARCH_LIMIT,
      });
      if (requestId !== requestIdRef.current) return;
      setResults(response.contacts);
      setTotalMatches(response.pagination.total);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setResults([]);
      setTotalMatches(0);
      setSearchError("Couldn't search the Address Book. Try again.");
    } finally {
      if (requestId === requestIdRef.current) setSearching(false);
    }
  }, [companyId]);

  // Filters (and Refresh) search immediately; the first run is the seeded search on open.
  useEffect(() => {
    if (!ready) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    void search();
  }, [ready, search, countryId, activeOnly, rankedOnly, rankedIdsForFilter, refreshCount]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  function handleSearchChange(next: string) {
    setSearchTerm(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void search();
    }, SEARCH_DEBOUNCE_MS);
  }

  async function handleLink(contactId: string, contactName: string) {
    setBusy({ kind: "link", contactId });
    setActionError(null);
    setWarning(null);
    setNotice(null);
    try {
      const response = await submissionsService.updateEntryContact(
        submissionId,
        entryId,
        contactId,
      );
      setHistoryVersion(version => version + 1);
      onChanged();
      if (response.warning === "contact_not_at_firm") {
        // Still linked — lawyers move firms — but worth a second look, so stay open.
        setEntry(response.entry);
        setWarning(
          `Linked to ${contactName}, but they have no active role at ${
            submission.firmName || "this firm"
          }. Check this is the right person.`,
        );
        setBusy(null);
        return;
      }
      onClose();
    } catch (err) {
      setActionError(errorMessageFor(err, "Couldn't link this contact. Try again."));
      setBusy(null);
    }
  }

  async function handleClear() {
    setBusy({ kind: "clear" });
    setActionError(null);
    setWarning(null);
    setNotice(null);
    try {
      await submissionsService.updateEntryContact(submissionId, entryId, null);
      setHistoryVersion(version => version + 1);
      onChanged();
      onClose();
    } catch (err) {
      setActionError(errorMessageFor(err, "Couldn't save. Try again."));
      setBusy(null);
    }
  }

  async function handleConfirmUnlink() {
    setActionError(null);
    setWarning(null);
    setNotice(null);
    try {
      await submissionsService.updateEntryContact(submissionId, entryId, null);
    } catch (err) {
      throw new Error(errorMessageFor(err, "Couldn't unlink this contact. Try again."));
    }
    setHistoryVersion(version => version + 1);
    onChanged();
    onClose();
  }

  function handleDismiss() {
    if (!confirmingUnlink) onClose();
  }

  async function handleRematch() {
    setBusy({ kind: "rematch" });
    setActionError(null);
    setWarning(null);
    setNotice(null);
    try {
      const response = await submissionsService.rematchEntryContact(submissionId, entryId);
      setEntry(response.entry);
      setHistoryVersion(version => version + 1);
      onChanged();
      const state = getContactLinkState(response.entry);
      setNotice(
        `Auto-match re-run: ${state ? CONTACT_LINK_STATE_LABELS[state] : response.entry.contactMatchStatus}${
          response.entry.contactName ? ` (${response.entry.contactName})` : ""
        }.`,
      );
    } catch (err) {
      setActionError(errorMessageFor(err, "Couldn't re-run the auto-match. Try again."));
    } finally {
      setBusy(null);
    }
  }

  const countryOptions = useMemo(() => countriesOf(offices), [offices]);
  // The submission's own office first, then an office in the category country; with
  // neither, buildCreateContactUrl falls back to the company route.
  const createOfficeId = useMemo(
    () => submission.officeId ?? officeForCountry(offices, categoryCountry)?.id ?? null,
    [submission.officeId, offices, categoryCountry],
  );
  const createContactUrl = companyId
    ? buildCreateContactUrl({
        officeId: createOfficeId,
        companyId,
        personName,
        returnTo,
      })
    : null;

  const isBusy = busy !== null;
  const candidates = entry.contactMatchCandidates ?? [];
  const hasSearchTerm = searchTerm.trim() !== "";
  const isTruncated = totalMatches > results.length;
  const categoryCountryHasOffice =
    !categoryCountry || countryOptions.some(option => sameCountry(option.name, categoryCountry));
  const rankedDisabledReason =
    rankedState.status === "loading"
      ? null
      : practiceAreaId === null
        ? "This submission has no practice area"
        : !rankedAvailable
          ? "Ranking data unavailable"
          : null;
  const practice = getSubmissionPracticeFullName(submission);
  const firmLabel = company?.name || submission.firmName || "this firm";

  function linkButton(contactId: string, contactName: string) {
    if (entry.contactId === contactId) {
      return (
        <Button variant="outlined" color="default" size="small" disabled>
          Linked
        </Button>
      );
    }
    const isLinkingThis = busy?.kind === "link" && busy.contactId === contactId;
    return (
      <Button
        variant="outlined"
        color="default"
        size="small"
        disabled={isBusy}
        onClick={() => handleLink(contactId, contactName)}
        aria-label={`Link ${contactName}`}
      >
        {isLinkingThis ? "Linking..." : "Link"}
      </Button>
    );
  }

  return (
    <>
      <Modal isOpen={true} onClose={handleDismiss} title="Link contact" size="large" blurBackground>
        <div className={styles.body}>
          <div className={styles.header}>
            <p className={styles.sourceLine}>
              Firm entered: <span className={styles.sourceText}>“{personName || "—"}”</span>
            </p>
            <p className={styles.contextLine}>
              <span className={styles.contextStrong}>{submission.firmName || "Unknown firm"}</span>
              {" · "}
              {submission.publicationName || "Unknown publication"}
              {submission.edition ? ` ${submission.edition}` : ""}
              {practice && practice !== "-" ? ` · ${practice}` : ""}
              {categoryCountry ? ` · ${categoryCountry}` : ""}
            </p>
            <div className={styles.currentLine}>
              <span>Current:</span>
              <ContactMatchPill entry={entry} />
              {entry.contactId && (
                <Link
                  to={ROUTES.addressBook.contactDetail(entry.contactId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={badgeStyles.contactLink}
                >
                  {entry.contactName ?? "Linked contact"}
                </Link>
              )}
            </div>
          </div>

          {warning && (
            <p className={styles.warning} role="alert">
              {warning}
            </p>
          )}
          {notice && (
            <p className={styles.notice} role="status">
              {notice}
            </p>
          )}
          {actionError && (
            <p className={styles.error} role="alert">
              {actionError}
            </p>
          )}

          {preselected && (
            <section className={styles.section} aria-labelledby="link-contact-created-heading">
              <h3 id="link-contact-created-heading" className={styles.sectionTitle}>
                Newly created contact
              </h3>
              <table className={`${styles.table} ${styles.highlightTable}`}>
                <tbody>
                  <tr>
                    <td>{preselected.name}</td>
                    <td>{preselected.jobTitle || "—"}</td>
                    <td>{preselected.officeName || "—"}</td>
                    <td>{linkButton(preselected.id, preselected.name)}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          )}

          {candidates.length > 0 && (
            <section className={styles.section} aria-labelledby="link-contact-candidates-heading">
              <h3 id="link-contact-candidates-heading" className={styles.sectionTitle}>
                Suggested by auto-match
              </h3>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Job title</th>
                    <th>Office country</th>
                    <th>Why suggested</th>
                    <th>
                      <span className={styles.visuallyHidden}>Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map(candidate => (
                    <tr key={candidate.contactId}>
                      <td>{candidate.name}</td>
                      <td>{candidate.jobTitle || "—"}</td>
                      <td>{candidate.officeCountry || "—"}</td>
                      <td>
                        <EvidenceBadges candidate={candidate} />
                      </td>
                      <td>{linkButton(candidate.contactId, candidate.name)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          <section className={styles.section} aria-labelledby="link-contact-search-heading">
            <h3 id="link-contact-search-heading" className={styles.sectionTitle}>
              Contacts at {firmLabel}
            </h3>

            {firmLoading && (
              <p className={styles.loading} role="status">
                <span className={styles.spinner} aria-hidden="true" />
                Loading {submission.firmName || "the firm"} from the Address Book...
              </p>
            )}
            {companyState.status === "no_firm_ref" && (
              <p className={styles.hint}>
                This submission's account isn't approved yet, so it has no firm to search.
              </p>
            )}
            {companyState.status === "not_found" && (
              <div className={styles.notFound} role="status">
                <p className={styles.notFoundTitle}>
                  {submission.firmName || "This firm"} isn't in the Address Book
                </p>
                <p className={styles.hint}>
                  No company has firm ref {submission.firmRef}, so there are no contacts to search
                  or create. Ask Customer Success to add the firm, or record "No matching contact"
                  for now.
                </p>
              </div>
            )}
            {companyState.status === "error" && (
              <p className={styles.error} role="alert">
                Couldn't load the firm from the Address Book. Close and try again.
              </p>
            )}

            {ready && (
              <>
                <div className={styles.searchRow}>
                  <input
                    className={styles.searchInput}
                    type="text"
                    value={searchTerm}
                    onChange={event => handleSearchChange(event.target.value)}
                    placeholder="Search this firm's contacts by name..."
                    aria-label="Search contacts"
                  />
                  <Button
                    variant="outlined"
                    color="default"
                    size="small"
                    onClick={() => setRefreshCount(count => count + 1)}
                    disabled={!ready}
                  >
                    Refresh
                  </Button>
                </div>

                <div className={styles.filters}>
                  <label className={styles.filterField}>
                    <span className={styles.filterLabel}>Country</span>
                    <select
                      className={styles.select}
                      value={countryId}
                      onChange={event => setCountryId(event.target.value)}
                      disabled={!ready}
                    >
                      <option value={ANY_COUNTRY}>Any country</option>
                      {countryOptions.map(option => (
                        <option key={option.id} value={String(option.id)}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={styles.checkboxField}>
                    <input
                      type="checkbox"
                      checked={activeOnly}
                      onChange={event => setActiveOnly(event.target.checked)}
                      disabled={!ready}
                    />
                    Active roles only
                  </label>
                  <label className={styles.checkboxField} title={rankedDisabledReason ?? undefined}>
                    <input
                      type="checkbox"
                      checked={rankedOnly}
                      onChange={event => setRankedOnly(event.target.checked)}
                      disabled={!ready || !rankedAvailable || practiceAreaId === null}
                      aria-describedby={
                        rankedDisabledReason ? "link-contact-ranked-hint" : undefined
                      }
                    />
                    Ranked in practice area
                  </label>
                  {rankedDisabledReason && (
                    <span id="link-contact-ranked-hint" className={styles.filterHint}>
                      {rankedDisabledReason}
                    </span>
                  )}
                </div>

                {officesLoaded && !officesFailed && !categoryCountryHasOffice && (
                  <p className={styles.hint}>
                    This firm has no office in {categoryCountry}, so all countries are shown.
                  </p>
                )}
                {officesFailed && (
                  <p className={styles.hint}>
                    Couldn't load the firm's offices, so the country filter is unavailable.
                  </p>
                )}
                {searchError && (
                  <p className={styles.error} role="alert">
                    {searchError}
                  </p>
                )}

                <div className={styles.results} aria-live="polite" aria-busy={searching}>
                  {searching && <p className={styles.hint}>Searching...</p>}
                  {!searching && ready && results.length === 0 && !searchError && (
                    <p className={styles.hint}>
                      {hasSearchTerm
                        ? "Nobody matches with these filters. Clear the search box or a filter to widen it."
                        : "No contacts match these filters."}
                    </p>
                  )}
                  {!searching && results.length > 0 && (
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Job title</th>
                          <th>Office</th>
                          <th>Country</th>
                          <th>
                            <span className={styles.visuallyHidden}>Action</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map(contact => (
                          <tr key={contact.id}>
                            <td>
                              <span className={styles.nameWithBadge}>
                                {contact.name}
                                {rankedIdSet?.has(contact.id) && (
                                  <span
                                    className={`${badgeStyles.badge} ${badgeStyles.badgePositive}`}
                                  >
                                    Ranked in this area
                                  </span>
                                )}
                              </span>
                            </td>
                            <td>{contact.jobTitle || "—"}</td>
                            <td>{contact.officeName || "—"}</td>
                            <td>{contact.countryName || "—"}</td>
                            <td>{linkButton(contact.id, contact.name)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {!searching && isTruncated && (
                    <p className={styles.hint}>
                      Showing the first {results.length} of {totalMatches} matches — narrow the
                      search to see the rest.
                    </p>
                  )}
                </div>

                {createContactUrl && (
                  <p className={styles.createLine}>
                    Not in the Address Book?{" "}
                    <a href={createContactUrl} target="_blank" rel="noopener noreferrer">
                      Create new contact
                      <span className={styles.visuallyHidden}> (opens in a new tab)</span>
                    </a>
                    , then press Refresh.
                  </p>
                )}
              </>
            )}
          </section>

          {canReadHistory && (
            <ContactHistory
              submissionId={submissionId}
              entryId={entryId}
              refreshKey={historyVersion}
            />
          )}

          <div className={styles.footer}>
            <div className={styles.footerActions}>
              <Button
                variant="outlined"
                color="default"
                onClick={entry.contactId ? () => setConfirmingUnlink(true) : handleClear}
                disabled={isBusy}
              >
                {busy?.kind === "clear"
                  ? "Saving..."
                  : entry.contactId
                    ? "Unlink (no matching contact)"
                    : "No matching contact"}
              </Button>
              <Button variant="outlined" color="default" onClick={handleRematch} disabled={isBusy}>
                {busy?.kind === "rematch" ? "Re-running..." : "Re-run auto-match"}
              </Button>
            </div>
            <Button variant="outlined" color="default" onClick={onClose} disabled={isBusy}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
      {confirmingUnlink && (
        <ConfirmationModal
          title="Unlink contact?"
          message={`${entry.contactName ?? "This contact"} will be unlinked from this submission. Their editorial content for this submission will no longer be visible in the Research CMS.`}
          warningMessage=""
          confirmButtonText="Unlink"
          confirmButtonColor="error"
          onClose={() => setConfirmingUnlink(false)}
          onConfirm={handleConfirmUnlink}
        />
      )}
    </>
  );
}
